import { useState } from "react";
import { BarChart3, Check } from "lucide-react";
import { toast } from "sonner";
import { dataBR, fileUrl, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { DEV_STATUS, resultCls } from "@/components/ds/devStatus";

const formatTime = (min) => {
  const t = Math.round(Number(min) || 0);
  if (!t) return "—";
  const h = Math.floor(t / 60);
  const m = t % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

const consumo = (t) =>
  (t.filaments || []).reduce((s, i) => s + Number(i.quantity_g || 0), 0);

const realUsed = (t) =>
  (t.productions || []).reduce(
    (s, p) => s + (p.consumption || []).reduce((x, c) => x + Number(c.quantity_g || 0), 0),
    0
  ) || consumo(t);

const realWaste = (t) =>
  (t.productions || []).reduce(
    (s, p) => s + (p.extra_consumption || []).reduce((x, c) => x + Number(c.quantity_g || 0), 0),
    0
  ) || Number(t.waste_g || 0);

export function DevCompareModal({ tests = [], onClose }) {
  const ordered = [...tests].sort((a, b) => a.attempt - b.attempt);
  const [picked, setPicked] = useState(ordered.slice(-3).map((t) => t.id));
  const [step, setStep] = useState("select");

  const toggle = (id) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const selected = ordered.filter((t) => picked.includes(t.id));

  const rows = [
    ["Resultado", (t) => DEV_STATUS.RESULTS[t.result] || t.result || "Aguardando"],
    ["Material", (t) => (t.filaments || []).map((i) => i.filament_name).join(", ") || "—"],
    [
      "Cores / notas",
      (t) => (t.filaments || []).map((i) => i.color_note).filter(Boolean).join(", ") || "—",
    ],
    ["Plate", (t) => (t.plate_names || []).join(", ") || t.plate_name || "—"],
    ["Quantidade", (t) => `${t.quantity || t.quantity_produced || 0} un`],
    ["Tempo", (t) => formatTime(t.real_time_min || t.print_time_min)],
    [
      "Configurações",
      (t) =>
        Object.entries(t.settings || {})
          .filter(([, v]) => v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(" · ") || "—",
    ],
    ["Consumo real", (t) => gramas(realUsed(t))],
    ["Perda real", (t) => gramas(realWaste(t))],
    ["Data", (t) => dataBR(t.created_at)],
    ["Observações", (t) => t.result_notes || t.notes || "—"],
  ];

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Comparar testes"
      description="Escolha as tentativas para ver fotos e números lado a lado."
      testid="dev-compare-modal"
      wide={step === "compare"}
    >
      {step === "select" ? (
        <div className="mt-2 space-y-3">
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {ordered.map((t) => {
              const on = picked.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  data-testid={`compare-pick-${t.id}`}
                  className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    on ? "border-brand/50 bg-brand/10" : "border-white/10 bg-secondary/40"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      on ? "border-brand bg-brand text-background" : "border-white/25"
                    }`}
                  >
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      Teste #{t.attempt}
                      {t.plate_name ? ` · ${t.plate_name}` : ""}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {DEV_STATUS.RESULTS[t.result] || "Aguardando resultado"} ·{" "}
                      {dataBR(t.created_at)}
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] ${resultCls(t.result)}`}>
                    {DEV_STATUS.RESULTS[t.result] || "Aguardando"}
                  </span>
                </button>
              );
            })}
          </div>
          <Button
            onClick={() => {
              if (picked.length < 2) {
                toast.error("Escolha pelo menos 2 testes para comparar.");
                return;
              }
              setStep("compare");
            }}
            data-testid="run-compare-btn"
            className="h-12 w-full gap-2 rounded-full font-semibold transition-transform active:scale-95"
          >
            <BarChart3 className="h-4 w-4" /> Comparar ({picked.length})
          </Button>
        </div>
      ) : (
        <div className="mt-2 space-y-4">
          <div className="-mx-1 overflow-x-auto px-1">
            <div
              className="flex gap-3"
              style={{ minWidth: `${Math.max(selected.length * 210, 100)}px` }}
              data-testid="compare-columns"
            >
              {selected.map((t) => (
                <div
                  key={t.id}
                  className="w-[200px] shrink-0 rounded-xl border border-white/10 bg-secondary/30 p-3"
                  data-testid={`compare-col-${t.id}`}
                >
                  <p className="font-display text-sm">Teste #{t.attempt}</p>
                  <div className="mt-2 h-32 overflow-hidden rounded-lg border border-white/10 bg-background/40">
                    {(t.photos || [])[0] ? (
                      <img
                        src={fileUrl(t.photos[0])}
                        alt={`Teste ${t.attempt}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-muted-foreground">
                        Sem foto nesta tentativa
                      </div>
                    )}
                  </div>
                  <span
                    className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px] ${resultCls(t.result)}`}
                  >
                    {DEV_STATUS.RESULTS[t.result] || t.result}
                  </span>
                  <div className="mt-3 space-y-1.5">
                    {rows.slice(1).map(([label, fn]) => (
                      <div key={label} className="text-[11px]">
                        <p className="text-muted-foreground">{label}</p>
                        <p className="break-words text-xs">{fn(t)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setStep("select")}
            data-testid="back-to-select-btn"
            className="h-11 w-full rounded-full"
          >
            Escolher outros testes
          </Button>
        </div>
      )}
    </ResponsiveModal>
  );
}
